import { LOCALIZATIONS } from 'root/lib/constants';
import {
  getPracticeCurriculum,
  getLetterSoundsCurriculum,
  getCurriculum,
} from '../content/curriculum/CurriculumStorage';
import { getImageMap } from '../content/images/imageMap';

// mastery criteria for Picky Goblins
export const didMasterLevel = (questionsCorrect, totalQuestions) => (questionsCorrect / totalQuestions >= 0.80);

export const getTargets = (targets, itemExcluded) => {
  const areExcluding = targets.some(({ excludes }) => excludes);

  if (!areExcluding) return targets;

  if (itemExcluded) return targets.filter(({ questionId }) => questionId !== itemExcluded);

  const excluding = targets.reduce(
    (excludes, target) => (target.excludes ? [...excludes, target.excludes] : excludes),
    [],
  );
  const letterToExclude = excluding[Math.random() >= 0.5 ? 0 : 1];

  const filteredTargets = targets.filter(({ questionId }) => questionId !== letterToExclude);

  return filteredTargets;
};

// returns true if an array contains an item
export const arrayContainsItem = (array, item) => {
  for (let i = 0; i < array.length; i++) {
    if (array[i] === item) {
      return true;
    }
  }
  return false;
};

export const getCurrentLevelCompletionPercentage = (progress, level) => (
  (getProgressInLevel(level, progress) / getQuestionsInLevel(level)) * 100
);

export const getQuestionsInLevel = level => getQuestionsPerLevel()[level];

export const getQuestionsPerLevel = () => getCurriculum().levels.reduce(
  (questionsPerLevel, level) => [...questionsPerLevel, level.questions.length],
  [],
);

export const getProgressInLevel = (level, progress) => {
  const acc = getMaxAccumulatedProgress(level);
  const result = progress - acc;
  if (result < 0) {
    return 0;
  } if (result > 100) {
    return 100;
  }
  return result;
};

export const getMaxAccumulatedProgress = (level) => {
  const questionsPerLevel = getQuestionsPerLevel();
  const maxIterations = level < questionsPerLevel.length ? level : questionsPerLevel.length;
  return questionsPerLevel
    .slice(0, maxIterations)
    .reduce((acc, element) => acc + element, 0);
};

export const getProgressAtLevelStart = (targetLevel) => {
  const questionsPerLevel = getQuestionsPerLevel();

  const progress = questionsPerLevel.reduce((acc, questions, index) => {
    if (index >= targetLevel) return acc;

    return acc + questions;
  }, 0);

  return progress;
};

/**
 * Parses PracticeCurriculum.json file and returns an object containing all of words used across the different practice levels
 * Organized by the level they are seen in.
 * May contain duplicate words if they are scene in different levels. but that is what we need for level loading
 * @returns
 * {
 *   0: {
 *     word1: "word1",
 *     word2: "word2",
 *     word3: "word3",
 *     word4: "word4",
 *   }
 *   1: {
 *     word5: "word5",
 *     word6: "word6",
 *     word7: "word7",
 *     word8: "word8",
 *   }
 * }
 */
export const getWordAudioContentByLevel = () => {
  // build words list
  const levelLetters = getLevelLetters();
  const words = {};
  for (let levelIndex = 0; levelIndex < levelLetters.length; levelIndex++) {
    words[levelIndex] = {};
    for (let letterIndex = 0; letterIndex < levelLetters[levelIndex].length; letterIndex++) {
      const item = getPracticeCurriculum()[levelLetters[levelIndex][letterIndex]];
      words[levelIndex][item.instructionWord.audio.name] = item.instructionWord.audio;
      words[levelIndex][item.practice1TargetPicture1.audio.name] = item.practice1TargetPicture1.audio;
      words[levelIndex][item.practice1TargetPicture2.audio.name] = item.practice1TargetPicture2.audio;
      words[levelIndex][item.practice1TargetPicture3.audio.name] = item.practice1TargetPicture3.audio;
      words[levelIndex][item.practice1TargetPicture4.audio.name] = item.practice1TargetPicture4.audio;
      words[levelIndex][item.practice1DistractorPicture1.audio.name] = item.practice1DistractorPicture1.audio;
      words[levelIndex][item.practice1DistractorPicture2.audio.name] = item.practice1DistractorPicture2.audio;
      words[levelIndex][item.practice2TargetPicture1.audio.name] = item.practice2TargetPicture1.audio;
      words[levelIndex][item.practice2TargetPicture2.audio.name] = item.practice2TargetPicture2.audio;
      words[levelIndex][item.practice2TargetPicture3.audio.name] = item.practice2TargetPicture3.audio;
      words[levelIndex][item.practice2TargetPicture4.audio.name] = item.practice2TargetPicture4.audio;
      words[levelIndex][item.practice2DistractorPicture1.audio.name] = item.practice2DistractorPicture1.audio;
      words[levelIndex][item.practice2DistractorPicture2.audio.name] = item.practice2DistractorPicture2.audio;
      words[levelIndex][item.practice3TargetPicture1.audio.name] = item.practice3TargetPicture1.audio;
      words[levelIndex][item.practice3TargetPicture2.audio.name] = item.practice3TargetPicture2.audio;
      words[levelIndex][item.practice3TargetPicture3.audio.name] = item.practice3TargetPicture3.audio;
      words[levelIndex][item.practice3TargetPicture4.audio.name] = item.practice3TargetPicture4.audio;
      words[levelIndex][item.practice3DistractorPicture1.audio.name] = item.practice3DistractorPicture1.audio;
      words[levelIndex][item.practice3DistractorPicture2.audio.name] = item.practice3DistractorPicture2.audio;
    }
  }
  return words;
};

export const getLetterAudioContentByLevel = () => {
  const levelLetters = getLevelLetters();
  const sounds = {};

  for (let levelIndex = 0; levelIndex < levelLetters.length; levelIndex++) {
    sounds[levelIndex] = {};
    for (let letterIndex = 0; letterIndex < levelLetters[levelIndex].length; letterIndex++) {
      const item = getLetterSoundsCurriculum()[levelLetters[levelIndex][letterIndex]];
      sounds[levelIndex][`alyx_letter_${item.targetLetter}`] = item.alyxLetter;
      sounds[levelIndex][`alyx_phoneme_${item.targetLetter}`] = item.alyxPhoneme;
      sounds[levelIndex][`phoneme_${item.targetLetter}`] = item.phoneme;
    }
  }
  return sounds;
};

const PATH = {
  [LOCALIZATIONS.SPA_ES_DEFAULT]: 'picky_goblins/words/',
  [LOCALIZATIONS.EN]: 'shared/audio/words/',
  [LOCALIZATIONS.ES]: 'shared/audio/words/',
};

const graphemeToFile = (grapheme, language = LOCALIZATIONS.EN) => ({
  audio: {
    name: grapheme,
    file: `${grapheme}.mp3`,
    path: PATH[language],
  },
  image: grapheme,
});

export const graphemePracticeItemsToFile = (grapheme, language) => ({
  instructionWord: graphemeToFile(grapheme.instructionWord, language),
  practice1DistractorPicture1: graphemeToFile(grapheme.practice1DistractorPicture1, language),
  practice1DistractorPicture2: graphemeToFile(grapheme.practice1DistractorPicture2, language),
  practice1TargetPicture1: graphemeToFile(grapheme.practice1TargetPicture1, language),
  practice1TargetPicture2: graphemeToFile(grapheme.practice1TargetPicture2, language),
  practice1TargetPicture3: graphemeToFile(grapheme.practice1TargetPicture3, language),
  practice1TargetPicture4: graphemeToFile(grapheme.practice1TargetPicture4, language),
  practice2DistractorPicture1: graphemeToFile(grapheme.practice2DistractorPicture1, language),
  practice2DistractorPicture2: graphemeToFile(grapheme.practice2DistractorPicture2, language),
  practice2TargetPicture1: graphemeToFile(grapheme.practice2TargetPicture1, language),
  practice2TargetPicture2: graphemeToFile(grapheme.practice2TargetPicture2, language),
  practice2TargetPicture3: graphemeToFile(grapheme.practice2TargetPicture3, language),
  practice2TargetPicture4: graphemeToFile(grapheme.practice2TargetPicture4, language),
  practice3DistractorPicture1: graphemeToFile(grapheme.practice3DistractorPicture1, language),
  practice3DistractorPicture2: graphemeToFile(grapheme.practice3DistractorPicture2, language),
  practice3TargetPicture1: graphemeToFile(grapheme.practice3TargetPicture1, language),
  practice3TargetPicture2: graphemeToFile(grapheme.practice3TargetPicture2, language),
  practice3TargetPicture3: graphemeToFile(grapheme.practice3TargetPicture3, language),
  practice3TargetPicture4: graphemeToFile(grapheme.practice3TargetPicture4, language),
});

/**
 * Parses levelTargets.json and returns an array of levels that contain the letters seen in that level
 * first index maps directly to the level you are looking up. starts from zero.
 * @returns
 * [
 *   [a,b,c,d],
 *   [e,f,g,h],
 * ]
 */
export const getLevelLetters = () => (
  getCurriculum().levels.map(level => level.options.map(({ questionId }) => questionId))
);

/**
 * Get letters option by level in the curriculum.
 * @param {number} level curriculum level.
 * @returns array of letters by level.
 */
export const lettersByLevel = level => getLevelLetters()[level];

/**
 * Get letters image by level in the curriculum.
* @param {number} level curriculum level.
 * @returns array objects of images letters by level.
 */
export const getLettersImageByLevel = level =>
  lettersByLevel(level).map(l => getImageMap().letter[`Letter${l.toUpperCase()}`]);

/**
 * Get objects image by level in the curriculum.
 * @param {number} level curriculum level.
 * @returns array objects of images objects(name of figure) by level.
 */
export const getObjectsImageByLevel = level =>
  lettersByLevel(level).map(l => getImageMap().object[`Object${l.toUpperCase()}`]);

/**
 * Get practice image by level in the curriculum.
 * @param {number} level curriculum level.
 * @returns array objects of images practice by level.
 */
export const getPracticeImageByLevel = (level) => {
  const practiceItems = lettersByLevel(level).map(letter => getPracticeCurriculum()[letter]);

  const practiceImages = [];
  practiceItems.forEach((element) => {
    Object.values(element).forEach(({ image }) => {
      const imageItem = getImageMap().practice[image];
      if (imageItem) {
        practiceImages.push(imageItem);
      }
    });
  });
  return practiceImages;
};

/**
 * Get all images, letter,object and practices images objects  by level in the curriculum.
 * @param {number} level curriculum level.
 * @returns array objects of images letters,objects and practices.
 */
export const getImagesByLevel = level =>
  [...getLettersImageByLevel(level), ...getObjectsImageByLevel(level), ...getPracticeImageByLevel(level)];
